import { EditableText } from '@/components/editor/EditableText';
import { FICHE_COLOR_HEX } from '@/lib/colors';
import { renderMarkdownLite } from '@/lib/markdown';
import type { DrugCard, Fiche, FicheColor, FicheSection, MiniHeader } from '@pharmafiche/schema';
import { useMemo } from 'react';
import type { FicheEditHandlers } from './FicheDessinee';

interface FicheComparativeProps {
  fiche: Fiche;
  editHandlers?: FicheEditHandlers;
}

/**
 * Phase 6 — comparative template.
 *
 * The table dominates. White background with a single pale accent stripe at
 * the top. DM Serif Display title, IBM Plex Sans body, IBM Plex Mono for any
 * numeric/posology values. Row labels in left column (caps), columns are
 * molecules, each with a thin top accent border in the molecule's color.
 *
 * If the fiche has no `comparison_table` section we auto-synthesise one from
 * the drug_cards (one column per molecule, rows for Mécanisme / Posologie / EI
 * / etc. derived from their sub_sections). The original drug_cards still
 * render below the table in a compact form.
 */
export function FicheComparative({ fiche, editHandlers }: FicheComparativeProps) {
  const drugCards = useMemo(
    () => fiche.sections.filter((s) => s.type === 'drug_card'),
    [fiche.sections],
  );

  const explicitTable = fiche.sections.find((s) => s.type === 'comparison_table');
  const synthTable = useMemo(
    () => (drugCards.length >= 2 ? synthesizeFromDrugCards(drugCards) : null),
    [drugCards],
  );

  return (
    <div
      className="min-h-screen bg-white text-ink"
      style={{ WebkitFontSmoothing: 'antialiased', fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <AccentStripe
        colors={drugCards.map((s) => (s.type === 'drug_card' ? s.drug.color : 'neutral'))}
      />
      <div className="max-w-[960px] mx-auto px-6 md:px-10 pt-6 pb-12">
        <header className="mb-6">
          <h1 className="font-dm text-[32px] leading-tight" style={{ letterSpacing: '-0.01em' }}>
            <EditableText
              value={fiche.meta.title}
              plain
              ariaLabel="Titre"
              onCommit={(t) => editHandlers?.updateMetaTitle?.(t)}
            />
          </h1>
          {fiche.meta.subtitle ? (
            <p className="text-[14px] text-ink-soft mt-1">{fiche.meta.subtitle}</p>
          ) : null}
        </header>

        {/* The hero table */}
        {explicitTable && explicitTable.type === 'comparison_table' ? (
          <RenderTable
            columns={explicitTable.columns}
            rows={explicitTable.rows}
            colors={drugCards.map((s) => (s.type === 'drug_card' ? s.drug.color : 'neutral'))}
          />
        ) : synthTable ? (
          <RenderTable {...synthTable} />
        ) : (
          <p className="italic text-ink-soft text-[14px] mb-4">
            Ajoute au moins 2 médicaments pour comparer.
          </p>
        )}

        {/* À retenir / takeaways */}
        <h2 className="font-dm text-[20px] mt-8 mb-2">À retenir</h2>
        <ul className="m-0 pl-4 list-disc">
          {takeaways(fiche).map((t) => (
            <li key={`tk-${t}`} className="text-[14px] leading-[1.55]">
              {renderMarkdownLite(t)}
            </li>
          ))}
        </ul>

        {/* Compact drug cards */}
        <h2 className="font-dm text-[20px] mt-8 mb-2">Détails par molécule</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {drugCards.map((sec) =>
            sec.type === 'drug_card' ? (
              <CompactDrugCard key={sec.id} section={sec} editHandlers={editHandlers} />
            ) : null,
          )}
        </div>

        {/* Other non-drug sections (mnemonic, warning, intro, clinical_case) */}
        <OtherSections fiche={fiche} editHandlers={editHandlers} />
      </div>
    </div>
  );
}

function AccentStripe({ colors }: { colors: FicheColor[] }) {
  if (colors.length === 0) {
    return <div style={{ height: 6, background: '#e5e7eb' }} aria-hidden />;
  }
  return (
    <div
      className="grid"
      style={{ height: 6, gridTemplateColumns: `repeat(${colors.length}, 1fr)` }}
    >
      {colors.map((c, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: same color can repeat across columns
          key={`${c}-${i}`}
          style={{ background: FICHE_COLOR_HEX[c], opacity: 0.6 }}
        />
      ))}
    </div>
  );
}

interface TableShape {
  columns: string[];
  rows: { label: string; cells: string[] }[];
  colors: FicheColor[];
}

function RenderTable({ columns, rows, colors }: TableShape) {
  return (
    <table
      className="w-full text-[14px] border-collapse"
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      <thead>
        <tr>
          <th className="font-plex uppercase tracking-wider text-[11px] text-ink-soft text-left p-2">
            &nbsp;
          </th>
          {columns.map((c, i) => (
            <th
              key={c}
              className="font-plex font-bold text-left p-2"
              style={{
                borderTop: `3px solid ${FICHE_COLOR_HEX[colors[i] ?? 'neutral']}`,
                color: FICHE_COLOR_HEX[colors[i] ?? 'neutral'],
              }}
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className="hover:bg-paper/30 transition-colors">
            <td className="font-plex uppercase tracking-wider text-[11px] text-ink-soft p-2 border-b border-ink/15">
              {r.label}
            </td>
            {r.cells.map((c, j) => (
              <td key={`${r.label}-${j}`} className="p-2 border-b border-ink/15 align-top">
                <span
                  style={{
                    fontFamily: isNumeric(c) ? '"IBM Plex Mono", monospace' : undefined,
                  }}
                >
                  {renderMarkdownLite(c)}
                </span>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function isNumeric(text: string): boolean {
  return /\b\d/.test(text);
}

function CompactDrugCard({
  section,
  editHandlers,
}: {
  section: Extract<FicheSection, { type: 'drug_card' }>;
  editHandlers?: FicheEditHandlers;
}) {
  const { drug } = section;
  return (
    <article
      data-section-id={section.id}
      className="border border-ink/20 rounded-control p-3"
      style={{ borderTopColor: FICHE_COLOR_HEX[drug.color], borderTopWidth: 3 }}
    >
      <h3 className="font-dm text-[18px] mb-1" style={{ color: FICHE_COLOR_HEX[drug.color] }}>
        <EditableText
          value={drug.name}
          plain
          ariaLabel="Nom de la molécule"
          onCommit={(t) => editHandlers?.updateDrugName?.(section.id, t)}
        />
      </h3>
      <ul className="m-0 pl-4 list-disc text-[13px] leading-[1.55]">
        {drug.intro_bullets.map((b) => (
          <li key={b.id} data-bullet-id={b.id}>
            <EditableText
              value={b.text}
              ariaLabel="Bullet"
              onCommit={(t) => editHandlers?.updateBulletText?.(section.id, b.id, t)}
            />
          </li>
        ))}
      </ul>
    </article>
  );
}

function OtherSections({
  fiche,
  editHandlers,
}: {
  fiche: Fiche;
  editHandlers?: FicheEditHandlers;
}) {
  const others = fiche.sections.filter(
    (s) => s.type !== 'drug_card' && s.type !== 'comparison_table' && s.type !== 'diagram',
  );
  if (others.length === 0) return null;
  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      {others.map((sec) => {
        if (sec.type === 'mnemonic') {
          return (
            <aside
              key={sec.id}
              data-section-id={sec.id}
              className="rounded-control p-3"
              style={{
                background: 'rgba(46,108,181,0.06)',
                borderLeft: '3px solid #2e6cb5',
              }}
            >
              <div className="font-plex uppercase tracking-wider text-[11px] text-accent-blue mb-1">
                Mnémo
              </div>
              <p className="font-dm text-[17px] leading-[1.45]">
                <EditableText
                  value={sec.text}
                  boldColor="#1f2330"
                  ariaLabel="Mnémo"
                  onCommit={(t) => editHandlers?.updateMnemonicText?.(sec.id, t)}
                />
              </p>
            </aside>
          );
        }
        if (sec.type === 'warning') {
          return (
            <aside
              key={sec.id}
              data-section-id={sec.id}
              className="rounded-control p-3"
              style={{
                background: 'rgba(211,74,74,0.06)',
                borderLeft: '3px solid #d34a4a',
              }}
            >
              <div className="font-plex uppercase tracking-wider text-[11px] text-warn-text mb-1">
                Avertissement
              </div>
              <p className="text-[14px] leading-[1.55] text-warn-text">
                <EditableText
                  value={sec.text}
                  ariaLabel="Avertissement"
                  onCommit={(t) => editHandlers?.updateWarningText?.(sec.id, t)}
                />
              </p>
            </aside>
          );
        }
        if (sec.type === 'clinical_case') {
          return (
            <aside
              key={sec.id}
              data-section-id={sec.id}
              className="rounded-control p-3 col-span-1 md:col-span-2"
              style={{
                background: 'rgba(211,74,74,0.04)',
                borderLeft: '3px solid #d34a4a',
              }}
            >
              <div className="font-plex uppercase tracking-wider text-[11px] text-warn-text mb-1">
                Cas clinique
              </div>
              <p className="text-[14px] leading-[1.55]">
                <EditableText
                  value={sec.patient}
                  ariaLabel="Patient"
                  onCommit={(t) => editHandlers?.updateClinicalPatient?.(sec.id, t)}
                />
              </p>
              <ul className="m-0 pl-4 list-disc text-[13px] mt-1">
                {sec.prescription.map((p, i) => (
                  <li key={`${sec.id}-rx-${i}`}>{renderMarkdownLite(p)}</li>
                ))}
              </ul>
              <p className="text-[14px] mt-1">
                <strong>Question : </strong>
                <EditableText
                  value={sec.question}
                  ariaLabel="Question"
                  onCommit={(t) => editHandlers?.updateClinicalQuestion?.(sec.id, t)}
                />
              </p>
              <div className="font-plex uppercase tracking-wider text-[11px] text-ink-soft mt-2 mb-1">
                Conduite
              </div>
              <ul className="m-0 pl-4 list-disc text-[13px]">
                {sec.conduite.map((b) => (
                  <li key={b.id} data-bullet-id={b.id}>
                    <EditableText
                      value={b.text}
                      ariaLabel="Bullet"
                      onCommit={(t) => editHandlers?.updateBulletText?.(sec.id, b.id, t)}
                    />
                  </li>
                ))}
              </ul>
            </aside>
          );
        }
        if (sec.type === 'intro' || sec.type === 'classification') {
          return (
            <aside
              key={sec.id}
              data-section-id={sec.id}
              className="rounded-control p-3"
              style={{
                background: '#fafafa',
                borderLeft: `3px solid ${FICHE_COLOR_HEX[sec.color]}`,
              }}
            >
              <div className="font-plex uppercase tracking-wider text-[11px] text-ink-soft mb-1">
                <EditableText
                  value={sec.title}
                  plain
                  ariaLabel="Titre"
                  onCommit={(t) => editHandlers?.updateSectionTitle?.(sec.id, t)}
                />
              </div>
              <ul className="m-0 pl-4 list-disc text-[13px] leading-[1.5]">
                {sec.bullets.map((b) => (
                  <li key={b.id} data-bullet-id={b.id}>
                    <EditableText
                      value={b.text}
                      ariaLabel="Bullet"
                      onCommit={(t) => editHandlers?.updateBulletText?.(sec.id, b.id, t)}
                    />
                  </li>
                ))}
              </ul>
            </aside>
          );
        }
        return null;
      })}
    </div>
  );
}

/** Best-effort takeaways pulled from intro/classification bullets. */
function takeaways(fiche: Fiche): string[] {
  const out: string[] = [];
  for (const sec of fiche.sections) {
    if (sec.type === 'intro' || sec.type === 'classification') {
      for (const b of sec.bullets) out.push(b.text);
      if (out.length >= 5) break;
    }
  }
  return out.slice(0, 5);
}

/**
 * Auto-build a comparison table from drug_cards by aligning their
 * sub_section labels into rows. Sub-sections present in only some cards
 * still get a row, with empty cells where missing.
 */
function synthesizeFromDrugCards(drugCards: FicheSection[]): TableShape {
  const drugs = drugCards
    .filter((s): s is Extract<FicheSection, { type: 'drug_card' }> => s.type === 'drug_card')
    .map((s) => s.drug);

  const columns = drugs.map((d) => d.name);
  const colors = drugs.map((d) => d.color);

  // Collect all unique labels, preserving order across drugs
  const labels: string[] = [];
  for (const d of drugs) {
    for (const ss of d.sub_sections) {
      if (!labels.includes(ss.label)) labels.push(ss.label);
    }
  }

  // Also surface the intro bullets as a "Mécanisme" row at the top
  const rows: { label: string; cells: string[] }[] = [];
  rows.push({
    label: 'Mécanisme',
    cells: drugs.map((d) =>
      d.intro_bullets
        .map((b) => b.text.replace(/\*\*/g, ''))
        .slice(0, 1)
        .join(' '),
    ),
  });
  for (const label of labels) {
    rows.push({
      label: cleanLabel(label),
      cells: drugs.map((d) => {
        const ss: MiniHeader | undefined = d.sub_sections.find((x) => x.label === label);
        if (!ss) return '—';
        return ss.bullets
          .map((b) => b.text.replace(/\*\*/g, ''))
          .slice(0, 2)
          .join(' · ');
      }),
    });
  }

  return { columns, rows, colors };
}

function cleanLabel(label: string): string {
  // strip leading emoji
  return label
    .replace(/^\p{Emoji_Presentation}\s*/u, '')
    .replace(/^\p{Extended_Pictographic}\s*/u, '');
}

// Re-export for downstream code that imports the type
export type { DrugCard };
